// SYSTEM_ROUTE: /security
import { useState } from 'react';
import type { LockStatus, SecurityConfig, SecurityEvent } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { useAsync } from '../lib/useAsync.js';
import {
  cloudConfigured,
  cloudApiUrl,
  getApiOverride,
  setApiOverride,
  getSignedInEmail,
  isSignedIn,
  requestOtp,
  signOut,
  verifyOtp,
} from '../lib/cloud.js';

const STATUS_TONE: Record<LockStatus, ChipTone> = {
  unset: 'muted',
  locked: 'warn',
  unlocked: 'primary',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

export function SecurityScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();

  const status = useAsync<LockStatus>(() => services.security.status(), [revision]);
  const config = useAsync<SecurityConfig>(() => services.security.getConfig(), [revision]);
  const audit = useAsync<readonly SecurityEvent[]>(() => services.security.listAudit(20), [revision]);

  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [clearPass, setClearPass] = useState('');
  const [error, setError] = useState('');

  const cfg = config.value;
  const st = status.value ?? 'unset';

  const setPassphrase = async () => {
    setError('');
    if (pass.length < 4) return setError('Use at least 4 characters.');
    if (pass !== confirm) return setError('Passphrases do not match.');
    await services.security.setPassphrase(pass);
    setPass('');
    setConfirm('');
    refresh();
  };

  const clearPassphrase = async () => {
    setError('');
    const ok = await services.security.clearPassphrase(clearPass);
    if (!ok) return setError('Incorrect passphrase.');
    setClearPass('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Security & Lock</>} subtitle={<>A local-first app lock for this device. Your passphrase is stored only as a PBKDF2 hash, never
            in plain text. Sign in to your cloud account below to enable cross-device sync.</>} actions={<><Chip tone={STATUS_TONE[st]}>{st}</Chip></>} />

      {error ? (
        <div style={{ color: 'var(--lc-error, #e06)', fontSize: 'var(--lc-text-sm)', padding: '0 4px' }}>{error}</div>
      ) : null}

      {st === 'unset' ? (
        <Pane title="Set a passphrase" hint="Protects this device's local data behind a lock.">
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="New passphrase" aria-label="New passphrase" style={{ ...inputStyle, flex: '1 1 180px' }} />
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm" aria-label="Confirm passphrase" style={{ ...inputStyle, flex: '1 1 180px' }} />
            <Button variant="primary" onClick={() => void setPassphrase()}>
              Set lock
            </Button>
          </div>
        </Pane>
      ) : (
        <>
          <Pane title="Lock" hint="The app locks on next load or when idle.">
            <Button variant="primary" onClick={() => void services.security.lock('manual').then(refresh)}>
              Lock now
            </Button>
          </Pane>

          <Pane title="Auto-lock" hint="Lock automatically after a period of inactivity.">
            <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>Idle minutes (0 = never)</span>
              <input
                type="number"
                min={0}
                value={cfg?.autoLockMinutes ?? 15}
                onChange={(e) => void services.security.setAutoLockMinutes(Math.max(0, Number(e.target.value) || 0)).then(refresh)}
                aria-label="Auto-lock minutes"
                style={{ ...inputStyle, width: 80 }}
              />
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--lc-text-sm)' }}>
                <input
                  type="checkbox"
                  checked={cfg?.requireStepUpForVault ?? true}
                  onChange={(e) => void services.security.setRequireStepUpForVault(e.target.checked).then(refresh)}
                  aria-label="Require step-up for vault"
                />
                Hide Finance vault until revealed
              </label>
            </div>
          </Pane>

          <Pane title="Remove lock" hint="Confirm with your current passphrase.">
            <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
              <input type="password" value={clearPass} onChange={(e) => setClearPass(e.target.value)} placeholder="Current passphrase" aria-label="Current passphrase" style={{ ...inputStyle, flex: '1 1 200px' }} />
              <Button variant="ghost" onClick={() => void clearPassphrase()}>
                Remove lock
              </Button>
            </div>
          </Pane>
        </>
      )}

      <Pane title="Security log" hint="Local audit — never leaves this device.">
        <Stack>
          {(audit.value ?? []).length === 0 ? (
            <EmptyState title="No security events yet">Setting a passphrase and unlocking are recorded here.</EmptyState>
          ) : (
            (audit.value ?? []).map((e) => (
              <CompactRow
                key={String(e.id)}
                title={e.eventType.replace(/_/g, ' ')}
                subtitle={new Date(e.at).toLocaleString()}
                meta={<Chip tone={e.eventType === 'failed_unlock' ? 'warn' : 'muted'}>{e.eventType === 'failed_unlock' ? 'failed' : 'ok'}</Chip>}
              />
            ))
          )}
        </Stack>
      </Pane>

      <SyncServerPane />
      <CloudAccountPane />
    </>
  );
}

/**
 * Runtime-configurable sync endpoint. Lets the owner point the app at a new API
 * URL (e.g. a fresh tunnel) without rebuilding — the override is stored locally
 * and wins over the build-time default.
 */
function SyncServerPane() {
  const [server, setServer] = useState(getApiOverride());
  const [saved, setSaved] = useState(false);
  const effective = cloudApiUrl();
  const save = () => {
    setApiOverride(server);
    setSaved(true);
    setTimeout(() => window.location.reload(), 600);
  };
  return (
    <Pane title="Sync server" hint="Where this device syncs. Change it any time — no rebuild needed.">
      <Stack>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="url"
            value={server}
            onChange={(e) => {
              setServer(e.target.value);
              setSaved(false);
            }}
            placeholder={effective ?? 'https://your-api.example.com'}
            aria-label="Sync server URL"
            style={{ ...inputStyle, flex: '1 1 300px' }}
          />
          <Button variant="primary" onClick={save}>Save</Button>
          {server ? (
            <Button variant="ghost" onClick={() => { setServer(''); setApiOverride(''); setSaved(true); setTimeout(() => window.location.reload(), 600); }}>
              Reset
            </Button>
          ) : null}
        </div>
        <div className="page-sub">
          Active endpoint: {effective ?? 'none (fully local)'}{server ? '' : ' · using build default'}
        </div>
        {saved ? <Chip tone="primary">Saved — reloading…</Chip> : null}
      </Stack>
    </Pane>
  );
}

/**
 * Cloud account (our own auth): email -> one-time code -> our signed JWT.
 * No third-party identity provider. Signing in unlocks cross-device sync; the
 * app stays fully usable offline and local-first without it.
 */
function CloudAccountPane() {
  const [email, setEmail] = useState(getSignedInEmail() ?? '');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [tone, setTone] = useState<ChipTone>('muted');
  const [signedIn, setSignedIn] = useState(isSignedIn());

  const configured = cloudConfigured();

  const send = async () => {
    setBusy(true);
    setMsg('');
    const res = await requestOtp(email);
    setBusy(false);
    if (!res.ok) {
      setTone('warn');
      setMsg(res.error ?? 'Could not send a code.');
      return;
    }
    setChallengeId(res.data!.challengeId);
    setTone('primary');
    setMsg('Code sent. Check your email and enter it below.');
  };

  const verify = async () => {
    if (!challengeId) return;
    setBusy(true);
    setMsg('');
    const res = await verifyOtp(challengeId, code, email);
    setBusy(false);
    if (!res.ok) {
      setTone('warn');
      setMsg(res.error ?? 'That code did not work.');
      return;
    }
    setSignedIn(true);
    setChallengeId(null);
    setCode('');
    setTone('primary');
    setMsg('Signed in. Cross-device sync is now available in Runtime Status.');
  };

  const out = () => {
    signOut();
    setSignedIn(false);
    setChallengeId(null);
    setCode('');
    setTone('muted');
    setMsg('Signed out on this device. Local data stays here.');
  };

  if (!configured) {
    return (
      <Pane title="Cloud account" hint="Optional — unlocks cross-device sync.">
        <EmptyState title="Cloud sync is off">
          This build has no sync server configured (VITE_LIFEOS_SYNC_API_URL). Life OS works fully
          offline and local-first. Set the API URL and rebuild to enable email sign-in and sync.
        </EmptyState>
      </Pane>
    );
  }

  if (signedIn) {
    return (
      <Pane title="Cloud account" hint="Signed in with your own email-OTP — no third-party login.">
        <Stack>
          <CompactRow
            title="Signed in"
            subtitle={getSignedInEmail() ?? 'this device'}
            meta={<Chip tone="primary">active</Chip>}
          />
          <CompactRow
            title="Sign out"
            subtitle="Removes the token from this device. Your local data is untouched."
            meta={<Button variant="ghost" onClick={out}>Sign out</Button>}
          />
          {msg ? <Chip tone={tone}>{msg}</Chip> : null}
        </Stack>
      </Pane>
    );
  }

  return (
    <Pane title="Cloud account" hint="Sign in with a one-time email code to sync across devices.">
      <Stack>
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            aria-label="Email address"
            disabled={busy || challengeId !== null}
            style={{ ...inputStyle, flex: '1 1 220px' }}
          />
          <Button variant={challengeId ? 'ghost' : 'primary'} onClick={() => void send()}>
            {challengeId ? 'Resend code' : busy ? 'Sending…' : 'Email me a code'}
          </Button>
        </div>
        {challengeId ? (
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void verify();
              }}
              placeholder="6-digit code"
              aria-label="One-time code"
              style={{ ...inputStyle, flex: '1 1 160px' }}
            />
            <Button variant="primary" onClick={() => void verify()}>
              {busy ? 'Verifying…' : 'Verify & sign in'}
            </Button>
          </div>
        ) : null}
        {msg ? <Chip tone={tone}>{msg}</Chip> : null}
      </Stack>
    </Pane>
  );
}
